/**
 * In-memory evaluation of the shared predicate DSL.
 *
 * {@link evaluateScalar} owns the leaf truth table for every shared operator; {@link evaluateNode} owns the
 * `and`/`or`/`not` recursion once, delegating leaves to a domain-supplied evaluator. The tag engine evaluates
 * a single string; the driver query engine resolves `record[node.field]` first — both reuse this machinery.
 */

import { DateTime } from "luxon";

/**
 * A parsed predicate node: a scalar leaf or an `and`/`or`/`not` combinator. Read structurally.
 */
export type PredicateObject = Record<string, unknown>;

/**
 * Resolves a single leaf node to a boolean. Supplied per domain so combinator recursion stays generic.
 */
export type LeafEvaluator = (leaf: PredicateObject) => boolean;

/**
 * The instant an ISO timestamp names, or none when the value is not one.
 */
function parseTimestamp(value: string): DateTime | null {
  const at = DateTime.fromISO(value);
  return at.isValid ? at : null;
}

/**
 * How many minutes before `now` a timestamp lies, or none when the value is not a timestamp.
 */
function minutesBefore(value: string, now: number): number | null {
  const at = parseTimestamp(value);
  return at === null ? null : DateTime.fromMillis(now).diff(at).as("minutes");
}
const EARTH_RADIUS_KM = 6371.0088;
const KM_PER_MILE = 1.609344;

/**
 * The center and radius of a `withinRadius` leaf, as the schema shapes it.
 */
export type RadiusSpec = { latitude: number; longitude: number; radius: number; unit: "miles" | "km"; label?: string };

/**
 * Reads a `"latitude,longitude"` value; `null` when either half is missing or not a finite number in range.
 */
export function parseCoordinates(value: string): { latitude: number; longitude: number } | null {
  const [lat, lng, ...rest] = value.split(",");
  if (lat === undefined || lng === undefined || rest.length > 0) {
    return null;
  }
  const latitude = Number(lat.trim());
  const longitude = Number(lng.trim());
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
    return null;
  }
  return { latitude, longitude };
}

/**
 * Great-circle distance in kilometers between two points (haversine on a spherical earth).
 */
export function distanceKm(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }) {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = toRadians(b.latitude - a.latitude);
  const dLng = toRadians(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(a.latitude)) * Math.cos(toRadians(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Evaluates a scalar leaf against an already-resolved string value.
 *
 * Handles the shared operators: `eq` (exact), `inArray` (exact membership), `isEmpty` (empty or whitespace),
 * `olderThan`/`newerThan` (the value parsed as a timestamp against `now`), `gt`/`lt` (the value parsed as a number),
 * `withinRadius` (the value parsed as `"latitude,longitude"`, its great-circle distance to the center at most the
 * radius), `isTrue`/`isFalse` (constants, value ignored), and `regex` (compiled per call). A `regex` reaching eval has
 * already been validated for compileability at parse time; the `catch` is a defensive fail-closed only. A value
 * that does not parse as a timestamp, a number, or a coordinate pair fails the comparison operators rather than
 * throwing.
 *
 * @param node - the scalar leaf (extra keys such as the driver query engine's `field` are ignored).
 * @param value - the resolved string to test.
 * @param now - the evaluation instant in epoch ms for the time operators; defaults to the wall clock.
 * @throws when the node carries no recognized operator. Every stored/pasted predicate is schema-validated
 *   before it reaches eval, so an unknown operator here is a programmer error (e.g. a hand-built node using
 *   a dropped operator) — failing loud beats silently evaluating false.
 */
export function evaluateScalar(node: PredicateObject, value: string, now: number = Date.now()): boolean {
  if ("eq" in node) {
    return value === node["eq"];
  }
  if ("inArray" in node) {
    return (node["inArray"] as string[]).includes(value);
  }
  if ("isEmpty" in node) {
    return value.trim() === "";
  }
  if ("olderThan" in node) {
    const age = minutesBefore(value, now);
    return age !== null && age > (node["olderThan"] as { minutes: number }).minutes;
  }
  if ("newerThan" in node) {
    const age = minutesBefore(value, now);
    return age !== null && age <= (node["newerThan"] as { minutes: number }).minutes;
  }
  if ("gt" in node) {
    const n = value.trim() === "" ? Number.NaN : Number(value);
    return !Number.isNaN(n) && n > (node["gt"] as number);
  }
  if ("lt" in node) {
    const n = value.trim() === "" ? Number.NaN : Number(value);
    return !Number.isNaN(n) && n < (node["lt"] as number);
  }
  if ("withinRadius" in node) {
    const spec = node["withinRadius"] as RadiusSpec;
    const point = parseCoordinates(value);
    if (!point) {
      return false;
    }
    const radiusKm = spec.unit === "miles" ? spec.radius * KM_PER_MILE : spec.radius;
    return distanceKm(point, spec) <= radiusKm;
  }
  if ("isTrue" in node) {
    return true;
  }
  if ("isFalse" in node) {
    return false;
  }
  if ("regex" in node) {
    const spec = node["regex"] as { pattern: string; flags?: string };
    try {
      return new RegExp(spec.pattern, spec.flags).test(value);
    } catch {
      return false;
    }
  }
  throw new Error(`Unknown predicate operator: "${Object.keys(node).join(", ") || "(none)"}"`);
}

/**
 * Evaluates a predicate node, recursing through `and`/`or`/`not` and delegating leaves to `evalLeaf`.
 *
 * Empty `and` is vacuously true (`every`), empty `or` vacuously false (`some`).
 *
 * @param node - the predicate node to evaluate.
 * @param evalLeaf - resolves a leaf node to a boolean.
 */
export function evaluateNode(node: PredicateObject, evalLeaf: LeafEvaluator): boolean {
  if ("and" in node) {
    return (node["and"] as PredicateObject[]).every((child) => evaluateNode(child, evalLeaf));
  }
  if ("or" in node) {
    return (node["or"] as PredicateObject[]).some((child) => evaluateNode(child, evalLeaf));
  }
  if ("not" in node) {
    return !evaluateNode(node["not"] as PredicateObject, evalLeaf);
  }
  return evalLeaf(node);
}
